<template>
  <div class="relative w-full h-full">
    <!-- Header -->
    <div class="relative pb-32">
      <div class="px-4 pt-3 pb-3 bg-white rounded-lg shadow-xl overflow-hidden md:px-10 mx-auto w-full">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-4xl font-bold">Обратная связь</h1>
            <p class="text-blueGray-400 mt-2">Входящие запросы от пользователей</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="mx-auto w-full -mt-24 mb-12">
      <!-- Search -->
      <div class="bg-white rounded-lg shadow-xl p-6 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Поиск</label>
            <input
              v-model="search"
              @keyup.enter="currentPage = 1; fetchItems()"
              placeholder="Поиск по имени или описанию..."
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Статус</label>
            <select
              v-model="filterRead"
              @change="currentPage = 1; fetchItems()"
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Все</option>
              <option value="false">Непрочитанные</option>
              <option value="true">Прочитанные</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="bg-white rounded-lg shadow-xl overflow-hidden">
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="text-center">
            <i class="fas fa-spinner fa-spin text-4xl text-blueGray-400 mb-2"></i>
            <p class="text-blueGray-600">Загрузка...</p>
          </div>
        </div>

        <div v-else-if="items.length === 0" class="text-center py-12">
          <i class="fas fa-inbox text-4xl text-blueGray-300 mb-2"></i>
          <p class="text-blueGray-600">Запросов не найдено</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-blueGray-50 border-b border-blueGray-200">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">#</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Имя</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Контакт</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Описание</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Прочитано</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Дата</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in items" :key="item.id" class="border-b border-blueGray-200 hover:bg-blueGray-50 transition">
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ (currentPage - 1) * perPage + index + 1 }}</td>
                <td class="px-6 py-4 text-sm text-blueGray-900 font-semibold">{{ item.name }}</td>
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ item.contact }}</td>
                <td class="px-6 py-4 text-sm text-blueGray-600" style="max-width: 400px;">
                  <span :title="item.description">{{ item.description.length > 100 ? item.description.substring(0, 100) + '...' : item.description }}</span>
                </td>
                <td class="px-6 py-4 text-sm">
                  <span
                    class="px-3 py-1 rounded-full text-xs font-bold"
                    :class="item.is_read ? 'bg-emerald-100 text-emerald-800' : 'bg-yellow-100 text-yellow-800'"
                  >
                    {{ item.is_read ? 'Да' : 'Нет' }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ formatDate(item.created_at) }}</td>
                <td class="px-6 py-4 text-sm">
                  <button
                    v-if="!item.is_read"
                    @click="markRead(item)"
                    class="px-3 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition text-xs font-bold"
                  >
                    Прочитано
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6 flex justify-center items-center gap-4">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          &larr; Назад
        </button>
        <span class="px-4 py-2 text-gray-700">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          Вперед &rarr;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_USER_FEEDBACK, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from '@/const/uri';

export default {
  name: 'UserFeedbackIndex',
  data() {
    return {
      items: [],
      loading: false,
      currentPage: 1,
      totalPages: 1,
      perPage: DEFAULT_PER_PAGE,
      search: '',
      filterRead: '',
    };
  },
  mounted() {
    this.fetchItems();
  },
  methods: {
    async fetchItems() {
      this.loading = true;
      try {
        const params = { p: this.currentPage, page_size: this.perPage };
        if (this.search) params.search = this.search;
        if (this.filterRead !== '') params.is_read = this.filterRead;

        const response = await axios.get(API_USER_FEEDBACK.list, {
          params,
          headers: HEADERS_TOKEN_JSON(),
        });

        if (response.data?.data) {
          this.items = response.data.data.results || [];
          const count = response.data.data.count || 0;
          this.totalPages = Math.ceil(count / this.perPage) || 1;
        }
      } catch (error) {
        console.error('Error fetching feedback:', error);
      } finally {
        this.loading = false;
      }
    },

    async markRead(item) {
      try {
        await axios.post(
          `${API_USER_FEEDBACK.base}${item.id}/mark-read/`,
          {},
          { headers: HEADERS_TOKEN_JSON() }
        );
        item.is_read = true;
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchItems();
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>
